<template>
    <div class="form-area col-12 col-lg-auto">
        <!-- Modal to insert/edit a value -->
        <edit-modal
            v-model:is-open="isTargetModalOpen"
            :index="modalTargetIndex"
            :data="modalData"
            @save="handleSave"
        />

        <!-- Table listing all defined values -->
        <collapsible :title="$t('targets.form.list')" :is-open="true">
            <table>
                <thead>
                    <tr>
                        <th>{{ $t('targets.form.type') }}</th>
                        <th>{{ $t('targets.form.min') }}</th>
                        <th>{{ $t('targets.form.best') }}</th>
                        <th>{{ $t('targets.form.max') }}</th>
                        <th class="text-right">
                            <button type="button" class="btn dark circle" @click="openTargetModal(null)">
                                <img src="@/assets/pictos/plus.svg" :alt="$t('action.add')" />
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(target, index) in requestData.targets" :key="target">
                        <td>{{ $t(`targetType.${target.type}`) }}</td>
                        <td>{{ target.min }} {{ $t(`unit.${target.unit}`) }}</td>
                        <td>{{ target.best }} {{ $t(`unit.${target.unit}`) }}</td>
                        <td>{{ target.max }} {{ $t(`unit.${target.unit}`) }}</td>
                        <td class="text-right buttons-container">
                            <button type="button" class="btn link" @click="openTargetModal(index)">
                                <img src="@/assets/pictos/pencil.svg" :alt="$t('action.edit')" />
                            </button>
                            <button type="button" class="btn dark circle" @click="requestData.removeNthTarget(index)">
                                <img src="@/assets/pictos/minus.svg" :alt="$t('action.remove')" />
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </collapsible>
    </div>

    <div class="data-area col-12 col-lg">
        <!-- Modal to edit chart settings -->
        <chart-settings-modal
            :graph-settings-title="$t('chartSettings.groupName.targetSettings')"
            v-model:is-open="isSettingsModalOpen"
            v-model:graph-settings="chartStore.targetsSettings"
        />

        <concentration-chart
            :graph-settings="chartStore.targetsSettings"
            @open-settings="openSettingsModal()"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import Collapsible from '@/components/Collapsible.vue';
import EditModal from './EditModal.vue';
import { useRequestDataStore } from '@/store/request-data';
import { DrugTarget } from '@/service/models/request/computation/drug-target';
import { useMenuStateStore } from '@/store/menu-state';
import ChartSettingsModal from '@/components/ChartSettingsModal.vue';
import ConcentrationChart from '@/components/ConcentrationChart.vue';
import { useChartsStore } from '@/store/charts';

export default defineComponent({
    components: {
        ChartSettingsModal,
        Collapsible,
        ConcentrationChart,
        EditModal,
    },

    setup()
    {
        const requestData = useRequestDataStore();
        const menuState = useMenuStateStore();
        const chartStore = useChartsStore();

        const isTargetModalOpen = ref(false);
        const isSettingsModalOpen = ref(false);
        const modalTargetIndex = ref(0);
        const modalData = ref<DrugTarget|null>(null);

        function openTargetModal(targetIndex: number|null): void
        {
            if (targetIndex === null) {
                modalTargetIndex.value = requestData.targets.length;
                modalData.value = null;
            }
            else {
                modalTargetIndex.value = targetIndex;
                modalData.value = requestData.targets[targetIndex];
            }

            isTargetModalOpen.value = true;
        }

        function openSettingsModal(): void
        {
            isSettingsModalOpen.value = true;
        }

        function handleSave(payload: {index: number, data: DrugTarget})
        {
            // When insterting a new custom target, mark the menu entry as validated
            if (typeof requestData.targets[payload.index] === "undefined") {
                menuState.targetsValidated = true;
            }

            // Save data in the store
            requestData.targets[payload.index] = payload.data;
        }

        return {
            requestData,
            chartStore,
            openTargetModal, isTargetModalOpen, modalTargetIndex, modalData,
            handleSave,
            openSettingsModal, isSettingsModalOpen,
        };
    },
})
</script>
